import showroomService, {
  ShowroomData,
  ShowroomDataWithPossibleUndefineds,
  USER_LINK_SETTINGS,
  UserLink,
  UserLinkType,
} from 'commons/api/showroomService/showroomService';
import { checkTerm } from 'commons/api/blacklistService/blacklistService';
import { deepCopy } from 'commons/utils';
import { defineStore } from 'pinia';

export enum ValidationErrorType {
  BLOCKLIST_VALIDATION = 'blocklist_validation',
  USERLINK_VALIDATION = 'userlink_validation',
}

type State = {
  showroomData?: ShowroomData;
  originalShowroomData?: ShowroomData;
  validation: {
    descriptionLength: number;
    userLinkErrors: Partial<Record<UserLinkType, boolean>>;
    descriptionBlocklist: string[];
  };
};

export const validateUserLinkValue = (type: UserLinkType, value: string) => {
  const pattern = USER_LINK_SETTINGS[type].pattern;
  if (value?.length <= 0) return false;

  if (!pattern) return true;

  const regex = new RegExp(pattern);
  return regex.test(value);
};

export const useMarketplaceStore = defineStore('marketplace', {
  state: (): State => ({
    showroomData: undefined,
    originalShowroomData: undefined,
    validation: {
      descriptionLength: 1000,
      userLinkErrors: {},
      descriptionBlocklist: [],
    },
  }),

  getters: {
    hasChanged: (state) =>
      !state.showroomData ||
      !state.originalShowroomData ||
      JSON.stringify(state.showroomData) !==
        JSON.stringify(state.originalShowroomData),
  },

  actions: {
    setShowroomData(showroomData: ShowroomDataWithPossibleUndefineds) {
      this.showroomData = {
        description: '',
        userLinks: [],
        userImages: {
          hasBanner: false,
        },
        ...showroomData,
      };
      this.originalShowroomData = deepCopy(this.showroomData);
    },
    async setUserBanner(bannerData: ShowroomData['userImages']) {
      if (this.showroomData && this.originalShowroomData) {
        this.showroomData.userImages = {
          ...bannerData,
        };
        this.originalShowroomData.userImages = deepCopy(
          this.showroomData.userImages,
        );

        await showroomService.updateShowroomData({
          userImages: this.showroomData.userImages,
        });
      }
    },
    setShowroomDescription(description: ShowroomData['description']) {
      if (this.showroomData) {
        this.showroomData.description = description;
        this.validateDescriptionBlocklist(description);
      }
    },
    setUserLinkValue(type: UserLinkType, value: UserLink['value']) {
      if (!this.showroomData) return;

      const userLink = this.showroomData.userLinks.find(
        (link) => link.type === type,
      );
      if (userLink) {
        userLink.value = value;
      } else {
        this.showroomData.userLinks.push({
          type,
          value:
            value ||
            this.originalShowroomData?.userLinks.find(
              (link) => link.type === type,
            )?.value ||
            '',
        });
      }

      if (validateUserLinkValue(type, value)) {
        this.clearUserLinkError(type);
      }
    },
    removeUserLink(type: UserLinkType) {
      if (!this.showroomData) return;

      this.showroomData.userLinks = this.showroomData.userLinks.filter(
        (link) => link.type !== type,
      );
    },
    reorderUserLinks(fromIndex: number, toIndex: number) {
      if (!this.showroomData || fromIndex === toIndex) return;

      const userLinks = [...this.showroomData.userLinks];
      const [movedItem] = userLinks.splice(fromIndex, 1);
      userLinks.splice(toIndex, 0, movedItem);

      this.showroomData.userLinks = userLinks;
    },
    validateAllUserLinks() {
      if (!this.showroomData) return true;

      this.validation.userLinkErrors = {};

      let allValid = true;
      this.showroomData?.userLinks?.forEach((userLink) => {
        const isValid = validateUserLinkValue(userLink.type, userLink.value);
        if (!isValid) {
          this.validation.userLinkErrors[userLink.type] = true;
          allValid = false;
        }
      });

      return allValid;
    },
    clearUserLinkError(type: UserLinkType) {
      if (this.validation.userLinkErrors[type]) {
        delete this.validation.userLinkErrors[type];
      }
    },
    async validateDescriptionBlocklist(description: string) {
      try {
        const blocklistResult = await checkTerm({
          field: 'description',
          terms: description,
        });

        if (blocklistResult?.blockedTerms?.length) {
          this.validation.descriptionBlocklist =
            blocklistResult.blockedTerms[0].foundBlockedTerms;
        } else {
          this.validation.descriptionBlocklist = [];
        }
      } catch (_error) {
        this.validation.descriptionBlocklist = [];
      }
    },
    async fetchShowroomData() {
      const data = await showroomService.getShowroomData();
      this.setShowroomData(data);
    },
    async saveShowroomData() {
      if (!this.showroomData) return;

      await this.validateDescriptionBlocklist(this.showroomData.description);
      if (this.validation.descriptionBlocklist.length > 0) {
        throw new Error(ValidationErrorType.BLOCKLIST_VALIDATION);
      }

      const isValid = this.validateAllUserLinks();
      if (!isValid) {
        throw new Error(ValidationErrorType.USERLINK_VALIDATION);
      }

      const showroomData = await showroomService.updateShowroomData(
        this.showroomData,
      );
      this.setShowroomData(showroomData);
    },
    resetShowroomData() {
      this.showroomData = deepCopy(this.originalShowroomData);
      this.validation.userLinkErrors = {};
      this.validation.descriptionBlocklist = [];
    },
  },
});
